import { Grid, IconButton, ListItemIcon, ListItemText, Menu, MenuItem } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';

import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import React, { useEffect, useRef, useState } from 'react';

import { Edit, ExpandLess } from '@material-ui/icons';
import { Auto, CopyToClipboard, setCurrentVisitSession } from '../../core';
import { formatLocalDate } from '../../core/date-time';
import { documentResolver } from '../../core/document-resolver';
import { Diagnosis, Flag, Procedure, Visit, Worksheet, WorksheetSummary } from '../../core/domain';
import { Expansion, ExpansionDetails, ExpansionSummary } from '../../core/expansion';
import { personName, withTooltip } from '../../core/format';
import { HospitalistContext } from '../../core/hospitalist-context';
import { procedureSummary } from '../../core/procedure';
import { darkBlue, darkWhite, orange, originalOrange } from '../../core/theme';
import { VisitContext } from '../../core/visit-context';
import { WorksheetFilter, WorksheetRef, WorksheetSelectionApi } from '../../core/worksheet-selection-api';
import { StateIcon } from '../coding-list/facility-status';
import { FieldValue, LinkButton } from '../common/presentation';

type ElementLinkProps = {
  linkFn: (a: Diagnosis | Procedure) => string;
  onClick: (a: Diagnosis | Procedure, id?: string) => void;
  el?: Diagnosis | Procedure;
  id?: string;
  worksheetId?: string;
};
function ElementLink(props: ElementLinkProps) {
  const { linkFn, onClick, el, id, worksheetId } = props;
  const link = linkFn(el);
  return (
    <Grid style={{ width: '100%' }} container item direction="row">
      <Grid item>
        <LinkButton dataName={id} label={link} color={orange} onClick={() => onClick(el, worksheetId)} width={260} />
      </Grid>
    </Grid>
  );
}
type ElementListProps = ElementLinkProps & {
  label: string;
  addLabel: string;
  list: Diagnosis[] | Procedure[] | any;
  onAddAll?: (a?: Diagnosis[] | Procedure[] | any, id?: string) => void;
  id?: string;
  worksheetId?: string;
};

function ElementList(props: ElementListProps) {
  const { label, list, linkFn, onClick, addLabel, id, worksheetId } = props;

  const onAddAll = () => {
    if (props.onAddAll) {
      props.onAddAll(list, worksheetId);
    }
  };

  return (
    <Expansion defaultExpanded={true} style={{ width: '100%', paddingLeft: '6px' }} expanded={true}>
      <ExpansionSummary>
        <Grid container direction="row" style={{ width: '100%', paddingRight: 24 }}>
          <Grid item>
            <Typography variant="body2" color="textSecondary">
              {label}
            </Typography>
          </Grid>
          <Grid item style={{ flexGrow: 1 }} />
          <Grid item>
            <LinkButton dataName={id} color={originalOrange} label={addLabel} onClick={onAddAll} />
          </Grid>
        </Grid>
      </ExpansionSummary>
      <ExpansionDetails>
        <Grid style={{ width: '100%', paddingLeft: '6px' }} container direction="column">
          {list.map((v: any, i: number) => (
            <ElementLink key={i} el={v} id={id} worksheetId={worksheetId} linkFn={linkFn} onClick={onClick} />
          ))}
        </Grid>
      </ExpansionDetails>
    </Expansion>
  );
}

type WorksheetElementsProps = {
  context: VisitContext;
  worksheet: Worksheet;
  summary?: WorksheetSummary;
  expanded?: boolean;
  selected?: string;
  ownerForm?: any;
};

function ExpandButton({ onToggle }) {
  const [open, setOpen] = useState(false);
  const onClick = () => {
    const expanded = !open;
    onToggle && onToggle(expanded);
    setOpen(expanded);
  };
  return <IconButton onClick={onClick}>{open ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>;
}

function icdLink(a: Diagnosis) {
  return `${a.diagnosisCode} - ${a.dxDescription}`;
}

function cptLink(a: Procedure) {
  return `${procedureSummary(a, true, true)}`;
}

function WorksheetElements({ context, worksheet, summary, selected, ...rest }: WorksheetElementsProps) {
  const { accountNumber, serviceDate, diagnoses, procedures, workflowOwners, worksheetType, worksheetId } = worksheet;
  const [expanded, setExpanded] = useState(rest.expanded);

  const title = `${worksheetType} - ${formatLocalDate(serviceDate)}`;

  const ctx = context.hospitalistContext;
  const apis = ctx.apisById(worksheet.worksheetId);

  useEffect(() => setExpanded(rest.expanded), [rest.expanded]);

  const onCopy = () => ctx.copyWorksheet(worksheet, worksheetId);
  const selectedSummary = summary?.worksheetId === selected;
  return (
    <Expansion key={accountNumber} expanded={expanded} onClick={() => setExpanded(!expanded)}>
      <ExpansionSummary
        style={{
          boxSizing: 'border-box',
          borderBottom: selectedSummary ? 'solid 2px ' + originalOrange : undefined,
        }}
        expandIcon={<ExpandMoreIcon />}
      >
        <Grid container direction="row" style={{ flexWrap: 'nowrap' }} spacing={1} alignItems="center">
          {summary && (
            <Grid item>
              {' '}
              <StateIcon {...summary} />
            </Grid>
          )}

          {summary?.editable && (
            <Grid item>
              <Edit fontSize="small" htmlColor={summary?.worksheetId === selected ? darkBlue : '#aaa'} />
            </Grid>
          )}

          <Grid item>
            <Typography variant="subtitle2">{title}</Typography>
          </Grid>
          <Grid item style={{ flexGrow: 1 }} />
          <Grid item style={{ paddingTop: 6 }}>
            <CopyToClipboard tooltip={() => 'Copy Worksheet Id to Clipboard'} data={() => ({ worksheetId })} />
          </Grid>
          <Grid item>
            <LinkButton dataName="add-all-elements-btn" color={originalOrange} label="Add All" onClick={onCopy} />
          </Grid>
        </Grid>
      </ExpansionSummary>
      <ExpansionDetails
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <Grid
          container
          direction="column"
          style={{
            width: '100%',
            maxHeight: '70vh',
            overflowX: 'hidden',
            overflowY: 'scroll',
            flexWrap: 'nowrap',
            paddingLeft: '6px',
            paddingBottom: '12px ',
            backgroundColor: selectedSummary ? '#ffefdf' : darkWhite,
          }}
        >
          <Grid item style={{ width: '100%', padding: 6 }}>
            {summary.editable ? (
              <Auto {...apis.ownerForm.controls.owner} />
            ) : (
              worksheet.owningProvider && <FieldValue row={false} label="Worksheet Attending" value={personName(worksheet.owningProvider)} />
            )}
          </Grid>
          {workflowOwners?.length > 0 && (
            <Grid item style={{ padding: 6 }}>
              <FieldValue row={true} label="Workflow Owners" value={null} />
              {workflowOwners.map((v, i) => (
                <FieldValue containerStyle={{ paddingLeft: 6 }} row={true} key={'wf' + i} label={personName(v)} value={v.workflow} />
              ))}
            </Grid>
          )}

          <Grid item style={{ width: '100%' }}>
            {!!diagnoses?.length && (
              <ElementList
                list={diagnoses.filter((a) => a.active)}
                label="Diagnoses"
                addLabel="Add All ICD"
                id="add-all-icd-btn"
                linkFn={icdLink}
                onClick={(a) => ctx.addIcd(a, summary?.worksheetId)}
                onAddAll={(a) => ctx.allIcd(a, summary?.worksheetId)}
                worksheetId={summary?.worksheetId}
              />
            )}
          </Grid>
          <Grid item style={{ width: '100%' }}>
            {!!procedures?.length && (
              <ElementList
                list={procedures.filter((a) => a.active)}
                label="Procedures"
                addLabel="Add All CPT"
                id="add-all-cpt-btn"
                linkFn={cptLink}
                onClick={(a) => ctx.addCpt(a, summary?.worksheetId)}
                onAddAll={(a) => ctx.allCpt(a, summary?.worksheetId)}
                worksheetId={summary?.worksheetId}
              />
            )}
          </Grid>
        </Grid>
      </ExpansionDetails>
    </Expansion>
  );
}

type FilterWorksheetsProps = {
  onChange?: (a?: WorksheetFilter[]) => void;
  onExpand?: (expanded: boolean) => void;
};

function FilterWorksheets({ onChange, onExpand }: FilterWorksheetsProps) {
  const [values, setValues] = React.useState(() => []);

  const onClick = (_: any, value: WorksheetFilter[]) => {
    setValues(value);
    if (onChange) {
      onChange(value);
    }
  };
  return (
    <Grid container direction="row" spacing={2} alignContent="center">
      <Grid item style={{ marginTop: 12 }}>
        <div> Worksheets By</div>
      </Grid>
      <Grid item>
        <ToggleButtonGroup size="small" style={{ maxHeight: 24 }} value={values} onChange={onClick}>
          <ToggleButton id="sort-by-date-btn" value="date">
            Date
          </ToggleButton>
          <ToggleButton id="sort-by-owner-btn" value="owner">
            Owner
          </ToggleButton>
          <ToggleButton id="sort-by-type-btn" value="type">
            Type
          </ToggleButton>
        </ToggleButtonGroup>
        <ExpandButton onToggle={onExpand} />
      </Grid>
    </Grid>
  );
}

export function RelatedWorksheets(props: VisitContext) {
  const { hospitalistContext } = props;

  const tuples = hospitalistContext.data((s) => s.tuples);
  const [expanded, setExpanded] = useState(false);
  tuples.forEach((a) => (a[3] = expanded));

  const selectedId = tuples.length ? hospitalistContext.data((s) => s.selectedId) : null;
  const selection = tuples.length ? hospitalistContext.apisById(selectedId).context.selection : null;

  const onFilter = (a: WorksheetFilter[]) => {
    if (a.length) {
      selection.sortWorksheets(a);
    } else {
      hospitalistContext.buildTuples();
    }
  };
  const onExpand = (a: boolean) => {
    //TODO : move to sort context
    tuples.forEach((v) => (v[3] = a));
    setExpanded(a);
  };

  return (
    <div style={{ padding: '12px 12px 12px 24px' }}>
      <Grid container direction="column" style={{ width: '100%' }} spacing={2}>
        <Grid item style={{ width: '100%' }}>
          <FilterWorksheets onChange={onFilter} onExpand={onExpand} />
        </Grid>
      </Grid>

      <br />
      <div
        style={{
          width: '100%',
          height: '76vh',
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        {tuples.map((v) => (
          <WorksheetElements key={v[1].worksheetId} selected={selectedId} context={props} expanded={v[1].worksheetId === selectedId || expanded} worksheet={v[1]} summary={v[2]} />
        ))}
      </div>
    </div>
  );
}

function WorksheetFlags({ flags }) {
  return (
    <Grid item container direction="row" alignItems="center" justify="center" spacing={1} style={{ paddingTop: 12 }}>
      {flags &&
        flags.map((v: Flag) =>
          withTooltip(
            v.description,
            <Grid item>
              <div
                style={{
                  color: 'white',
                  backgroundColor: v.resolved ? '#44aa44' : '#888',
                  textAlign: 'center',
                  borderRadius: 2,
                  paddingTop: 1,
                  margin: 1,
                  fontSize: 8,
                  width: 18,
                }}
              >
                {v.code}
              </div>
            </Grid>
          )
        )}
    </Grid>
  );
}

type WorkheetSelectionMenuProps = {
  worksheet: Worksheet;
  visit: Visit;
  api: WorksheetSelectionApi;
  hospitalist: HospitalistContext;
};

export function WorksheetSelectionMenu(props: WorkheetSelectionMenuProps) {
  const { api, visit } = props;

  const components = api.data((s) => s.selected);
  const worksheet = components.worksheet || api.worksheetById(props.worksheet.worksheetId);
  const { worksheetType, worksheetId } = worksheet;

  const ref = useRef(null);
  const list = api.getByType(worksheetType);
  const useMenu = list.length > 1;

  const [anchorEl, setAnchorEl] = React.useState(null);

  const onClick = () => {
    setAnchorEl(ref.current);
  };

  const onClose = () => {
    setAnchorEl(null);
  };

  const onSelect = (a: WorksheetSummary) => () => {
    api.setSelected(a.worksheetId);
    visit.worksheetId = a.worksheetId;
    setCurrentVisitSession(visit, false);
    documentResolver.openWindow(visit);
    onClose();
  };

  const label = api.getSummaryMap(worksheetId) + ' - ' + worksheetType + ' - ' + formatLocalDate(worksheet.serviceDate);

  const stateInfo = api.data((s) => s.state) || components.stateInfo;
  const summary = { stateInfo };
  const open = Boolean(anchorEl);
  const sel = api.data((s) => s.selected);

  const marginTop: any = { marginTop: 6 };

  return (
    <div>
      {
        <Grid ref={ref} spacing={1} container direction="row" alignItems="center" style={{ flexWrap: 'nowrap' }}>
          <Grid item style={marginTop}>
            {withTooltip(summary?.stateInfo?.label, <StateIcon {...summary} />)}
          </Grid>
          <Grid item style={marginTop}>
            <Edit fontSize="small" htmlColor={darkBlue} />
          </Grid>
          {withTooltip(
            worksheet.worksheetId + ' ' + worksheet.departmentCode,
            <Grid item style={{ whiteSpace: 'nowrap' }}>
              {label}
            </Grid>
          )}

          {useMenu && (
            <Grid item style={{ paddingTop: 8 }}>
              {open ? <ExpandLess onClick={onClick} /> : <ExpandMoreIcon onClick={onClick}></ExpandMoreIcon>}
            </Grid>
          )}
        </Grid>
      }
      {useMenu && (
        <Menu
          variant="selectedMenu"
          anchorEl={anchorEl}
          anchorPosition={{ top: 136, left: 0 }}
          getContentAnchorEl={null}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          transformOrigin={{ vertical: 'top', horizontal: 'center' }}
          keepMounted
          open={open}
          onClose={onClose}
        >
          {list.map(({ summary }: WorksheetRef) => {
            const { worksheetId, editable, departmentCode } = summary;
            const comp = api.resources.components[worksheetId];
            const worksheet = comp.worksheet;
            const stateInfo = comp.stateInfo;

            return withTooltip(
              worksheetId + ' - ' + departmentCode,
              <MenuItem selected={sel?.worksheet?.worksheetId === worksheetId} key={summary.worksheetId} disabled={!editable} onClick={onSelect(summary)}>
                {<StateIcon {...api.summaryById(summary.worksheetId)} />}
                &nbsp;&nbsp;
                {summary?.editable && (
                  <>
                    <Edit fontSize="small" htmlColor={'#aaa'} />
                    &nbsp;&nbsp;
                  </>
                )}
                {summary?.flags?.length && (
                  <ListItemIcon>
                    <WorksheetFlags flags={summary.flags} />
                  </ListItemIcon>
                )}
                <ListItemText primary={formatLocalDate(worksheet.serviceDate) + ' - ' + stateInfo?.name} />
              </MenuItem>
            );
          })}
        </Menu>
      )}
    </div>
  );
}
